// Account Console (BP8 — KISS billing & sales console).
//
// ONE screen answers "how's the business" + ONE customers/licenses table.
// Spec §12: billing & sales personas, one console, plain words, money up front,
// no UUIDs front-and-centre. Five verbs reachable in ≤2 clicks (New… menu +
// per-row drill-in to the License detail page).
//
// Data: GET /api/v1/analytics/mrr (BP7, enriched in BP8 with name + usage-vs-pool
// + billing_state + trial fields). Reuses the dashboard's existing card/token
// styling — no new design system.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { personaBase } from '@/lib/persona';
import { apiRequest } from '@/lib/supabase';
import { IssueTrialDialog } from '@/components/account/IssueTrialDialog';
import { useCanMutate } from '@/context/PersonaProvider';
import {
  DollarSign, KeyRound, FlaskConical, TrendingUp, AlertTriangle,
  Plus, FilePlus2, ChevronDown, RefreshCw, ArrowUpDown,
} from 'lucide-react';

// ── Row contract (matches mrr.service MrrRow + BP8 enrichment) ───────────────
interface MrrRow {
  license_id: string;
  contract_id: string;
  customer_type: string | null;
  is_trial: boolean;
  status: string;
  total_mrr: number;
  overage_mrr: number;
  total_peak_overage: number;
  customer_name: string | null;
  license_key_id: string | null;
  scope: string | null;
  vendor_pool: number | null;
  vendor_usage: number;
  org_pool: number | null;
  org_usage: number;
  ciic_pool: number;
  ciic_usage: number;
  overage_allowance_pct: number;
  billing_state: string;
  trial_status: string | null;
  trial_expires_at: string | null;
  trial_days_left: number | null;
}
interface ByCustomerType { customer_type: string; license_count: number; mrr: number; }
interface MrrAnalytics {
  total_mrr: number;
  active_licenses: number;
  trials_active: number;
  conversion_rate: number; // 0..1
  overage_revenue: number;
  by_customer_type: ByCustomerType[];
  rows: MrrRow[];
}

// ── Human formatting ─────────────────────────────────────────────────────────
const money = (n: number) =>
  `$${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const pct = (n: number) => `${Math.round((n || 0) * 100)}%`;

// ── Billing-state badge (color-coded: included / metered / overage) ──────────
const STATE_RANK: Record<string, number> = { included: 0, metered: 1, overage: 2 };
const BILLING_SKIN: Record<string, { bg: string; fg: string; label: string }> = {
  included: { bg: 'var(--ps-pill-green-bg)', fg: 'var(--ps-pill-green-tx)', label: 'Included' },
  metered: { bg: 'var(--ps-pill-amber-bg)', fg: 'var(--ps-pill-amber-tx)', label: 'Metered' },
  overage: { bg: 'var(--ps-pill-red-bg)', fg: 'var(--ps-pill-red-tx)', label: 'Overage' },
};
function BillingBadge({ state }: { state: string }) {
  const s = BILLING_SKIN[state] || BILLING_SKIN.included;
  return (
    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: s.bg, color: s.fg }}>
      {s.label}
    </span>
  );
}

// ── Usage-vs-pool meter (green / amber ≥80% / red over) ──────────────────────
function Meter({ label, used, pool }: { label: string; used: number; pool: number | null }) {
  // pool null or 0 → unlimited / not applicable.
  const unlimited = pool == null || pool === 0;
  const ratio = unlimited ? 0 : used / pool;
  const over = !unlimited && used > pool;
  const color = over ? 'var(--score-danger)' : ratio >= 0.8 ? 'var(--score-warning)' : 'var(--score-good)';
  const widthPct = unlimited ? 0 : Math.min(Math.max(ratio * 100, 1), 100);
  return (
    <div style={{ minWidth: 110 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, color: 'var(--text-muted)', marginBottom: 3 }}>
        <span>{label}</span>
        <span style={{ color: over ? 'var(--score-danger)' : 'var(--text-secondary)', fontWeight: over ? 700 : 500 }}>
          {used.toLocaleString()}{unlimited ? ' / ∞' : ` / ${pool!.toLocaleString()}`}
        </span>
      </div>
      <div style={{ height: 5, background: 'var(--bg-muted)', borderRadius: 99 }}>
        {!unlimited && <div style={{ height: '100%', width: `${widthPct}%`, background: color, borderRadius: 99, transition: 'width .3s' }} />}
      </div>
    </div>
  );
}

// ── Top KPI card ─────────────────────────────────────────────────────────────
function Kpi({ icon: Icon, label, value, sub, color }: { icon: typeof DollarSign; label: string; value: string; sub?: string; color?: string }) {
  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <span style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '.05em', fontWeight: 600 }}>{label}</span>
        <Icon size={16} color={color || 'var(--accent)'} />
      </div>
      <div style={{ fontSize: 24, fontWeight: 700, color: color || 'var(--text-primary)', lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

type SortKey = 'name' | 'mrr' | 'billing' | 'vendor';

export function AccountConsolePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const base = personaBase(location.pathname);
  const canMutate = useCanMutate();
  const [data, setData] = useState<MrrAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trialOpen, setTrialOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('mrr');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await apiRequest<MrrAnalytics>('/analytics/mrr');
      setData(d);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load account data');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const rows = useMemo(() => {
    const list = [...(data?.rows || [])];
    list.sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'name') cmp = (a.customer_name || '').localeCompare(b.customer_name || '');
      else if (sortKey === 'mrr') cmp = a.total_mrr - b.total_mrr;
      else if (sortKey === 'billing') cmp = (STATE_RANK[a.billing_state] ?? 0) - (STATE_RANK[b.billing_state] ?? 0);
      else if (sortKey === 'vendor') {
        const ra = a.vendor_pool ? a.vendor_usage / a.vendor_pool : 0;
        const rb = b.vendor_pool ? b.vendor_usage / b.vendor_pool : 0;
        cmp = ra - rb;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [data, sortKey, sortDir]);

  const toggleSort = (k: SortKey) => {
    if (sortKey === k) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(k); setSortDir(k === 'name' ? 'asc' : 'desc'); }
  };

  // Count of rows needing attention (over-limit OR in overage) — the sales/billing flag.
  const attentionCount = rows.filter(
    (r) => r.billing_state === 'overage' || (r.vendor_pool != null && r.vendor_pool > 0 && r.vendor_usage > r.vendor_pool)
  ).length;

  const btnSec: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8,
    background: 'var(--surface)', color: 'var(--text-primary)', border: '1px solid var(--border)',
    fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
  };
  const th: React.CSSProperties = { padding: '10px 14px', textAlign: 'left', fontSize: 10.5, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '.04em', whiteSpace: 'nowrap' };
  const sortableTh = (label: string, k: SortKey): React.CSSProperties => ({ ...th, cursor: 'pointer', userSelect: 'none', color: sortKey === k ? 'var(--accent)' : 'var(--text-muted)' });

  if (loading) return (
    <div style={{ padding: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', height: 360 }}>
      <div style={{ textAlign: 'center' }}>
        <div className="animate-spin" style={{ width: 32, height: 32, border: '2px solid var(--accent)', borderTopColor: 'transparent', borderRadius: '50%', margin: '0 auto 12px' }} />
        <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Loading the account book…</p>
      </div>
    </div>
  );
  if (error) return (
    <div style={{ padding: '24px 32px' }}>
      <div style={{ padding: '16px 18px', background: 'var(--ps-pill-red-bg)', border: '1px solid var(--ps-pill-red-tx)', borderRadius: 8, color: 'var(--ps-pill-red-tx)', display: 'flex', alignItems: 'center', gap: 10 }}>
        <AlertTriangle size={18} /> {error}
        <button onClick={load} style={{ marginLeft: 'auto', ...btnSec }}><RefreshCw size={13} /> Retry</button>
      </div>
    </div>
  );

  const d = data!;
  const empty = rows.length === 0;

  return (
    <div style={{ padding: '24px 32px' }}>
      {/* Header + the "New…" verb menu (verbs 1 & 2 in ≤2 clicks) */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-.01em', margin: 0 }}>Account</h1>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '4px 0 0' }}>
            Money, customers and trials — one console for billing &amp; sales.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={load} style={btnSec}><RefreshCw size={13} /> Refresh</button>
          {canMutate && (
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setMenuOpen((o) => !o)}
                style={{ ...btnSec, background: 'var(--accent)', color: 'white', border: '1px solid var(--accent)' }}
              >
                <Plus size={14} /> New… <ChevronDown size={13} />
              </button>
              {menuOpen && (
                <>
                  <div onClick={() => setMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
                  <div style={{ position: 'absolute', right: 0, top: '110%', zIndex: 50, background: 'var(--surface-raised)', border: '1px solid var(--border)', borderRadius: 10, boxShadow: 'var(--shadow-menu)', minWidth: 200, overflow: 'hidden' }}>
                    <button
                      onClick={() => { setMenuOpen(false); navigate('/sales/licenses/new'); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '11px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, textAlign: 'left' }}
                    >
                      <FilePlus2 size={15} color="var(--accent)" /> Issue License
                    </button>
                    <button
                      onClick={() => { setMenuOpen(false); setTrialOpen(true); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '11px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, textAlign: 'left', borderTop: '1px solid var(--border)' }}
                    >
                      <FlaskConical size={15} color="var(--ps-pill-purple-tx)" /> Issue Trial
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Five top cards — "how's the business" */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 14, marginBottom: 18 }}>
        <Kpi icon={DollarSign} label="Total MRR" value={money(d.total_mrr)} sub="Monthly recurring" color="var(--accent)" />
        <Kpi icon={KeyRound} label="Active Licenses" value={d.active_licenses.toLocaleString()} sub="Paying customers" />
        <Kpi icon={FlaskConical} label="Active Trials" value={d.trials_active.toLocaleString()} sub="In the pipeline" color="var(--ps-pill-purple-tx)" />
        <Kpi icon={TrendingUp} label="Trial → Paid" value={pct(d.conversion_rate)} sub="Conversion rate" color="var(--ps-pill-green-tx)" />
        <Kpi icon={AlertTriangle} label="Overage Revenue" value={money(d.overage_revenue)} sub="This period" color={d.overage_revenue > 0 ? 'var(--ps-pill-amber-tx)' : 'var(--text-muted)'} />
      </div>

      {/* By-customer-type breakdown */}
      {d.by_customer_type.length > 0 && (
        <div className="card" style={{ marginBottom: 18, padding: '14px 18px' }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '.05em', marginBottom: 10 }}>MRR by customer type</div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {d.by_customer_type.map((c) => {
              const share = d.total_mrr > 0 ? c.mrr / d.total_mrr : 0;
              return (
                <div key={c.customer_type} style={{ flex: '1 1 160px', minWidth: 160, border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', background: 'var(--bg-secondary)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                    <span style={{ fontSize: 12.5, fontWeight: 600, textTransform: 'capitalize' }}>{c.customer_type}</span>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{c.license_count} lic.</span>
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 700 }}>{money(c.mrr)}</div>
                  <div style={{ height: 4, background: 'var(--bg-muted)', borderRadius: 99, marginTop: 6 }}>
                    <div style={{ height: '100%', width: `${Math.round(share * 100)}%`, background: 'var(--accent)', borderRadius: 99 }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Attention banner — surfaces upsell + revenue-leak at a glance */}
      {attentionCount > 0 && (
        <div style={{ padding: '10px 14px', background: 'var(--ps-pill-amber-bg)', borderRadius: 8, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--ps-pill-amber-tx)' }}>
          <AlertTriangle size={14} />
          <strong>{attentionCount} customer{attentionCount !== 1 ? 's' : ''}</strong> over pool or in overage — upsell or true-up opportunity.
        </div>
      )}

      {/* The customers / licenses table */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 14, fontWeight: 600 }}>Customers &amp; licenses</span>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{rows.length} total</span>
        </div>
        {empty ? (
          <div style={{ padding: '36px 18px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
            No licenses yet.{canMutate && <> Use <strong>New…</strong> to issue a license or a trial.</>}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-muted)' }}>
                  <th style={sortableTh('Customer', 'name')} onClick={() => toggleSort('name')}>
                    Customer <ArrowUpDown size={10} style={{ verticalAlign: 'middle' }} />
                  </th>
                  <th style={th}>Type</th>
                  <th style={sortableTh('Vendors', 'vendor')} onClick={() => toggleSort('vendor')}>Usage vs pool</th>
                  <th style={sortableTh('Billing', 'billing')} onClick={() => toggleSort('billing')}>Billing</th>
                  <th style={{ ...sortableTh('MRR', 'mrr'), textAlign: 'right' }} onClick={() => toggleSort('mrr')}>
                    MRR <ArrowUpDown size={10} style={{ verticalAlign: 'middle' }} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const over = r.billing_state === 'overage' || (r.vendor_pool != null && r.vendor_pool > 0 && r.vendor_usage > r.vendor_pool);
                  const isTrial = r.is_trial || r.trial_status === 'active';
                  return (
                    <tr
                      key={r.license_id}
                      onClick={() => navigate(`${base}/licenses/${r.license_id}`, { state: { from: { type: 'console' } } })}
                      style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer', background: over ? 'var(--ps-pill-amber-bg)' : 'transparent' }}
                    >
                      {/* Name + scope, no UUID front-and-centre */}
                      <td style={{ padding: '12px 14px' }}>
                        <div style={{ fontSize: 13.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                          {r.customer_name || 'Unnamed account'}
                          {isTrial && (
                            <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 99, background: 'var(--ps-pill-purple-bg)', color: 'var(--ps-pill-purple-tx)' }}>
                              TRIAL{r.trial_days_left != null ? ` · ${Math.max(r.trial_days_left, 0)}d` : ''}
                            </span>
                          )}
                          {r.trial_status === 'converted' && (
                            <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 99, background: 'var(--ps-pill-green-bg)', color: 'var(--ps-pill-green-tx)' }}>CONVERTED</span>
                          )}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, textTransform: 'capitalize' }}>
                          {r.scope || 'org'} · {r.status}
                        </div>
                      </td>
                      <td style={{ padding: '12px 14px', fontSize: 12.5, textTransform: 'capitalize', color: 'var(--text-secondary)' }}>
                        {r.customer_type || '—'}
                      </td>
                      {/* Usage-vs-pool meters: vendors + orgs (+CIIC if any) */}
                      <td style={{ padding: '12px 14px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          <Meter label="Vendors" used={r.vendor_usage} pool={r.vendor_pool} />
                          {(r.org_pool != null && r.org_pool > 0) && <Meter label="Orgs" used={r.org_usage} pool={r.org_pool} />}
                          {(r.ciic_pool > 0) && <Meter label="CIIC" used={r.ciic_usage} pool={r.ciic_pool} />}
                        </div>
                      </td>
                      <td style={{ padding: '12px 14px' }}><BillingBadge state={r.billing_state} /></td>
                      <td style={{ padding: '12px 14px', textAlign: 'right' }}>
                        <div style={{ fontSize: 14, fontWeight: 700 }}>{money(r.total_mrr)}</div>
                        {r.overage_mrr > 0 && (
                          <div style={{ fontSize: 11, color: 'var(--ps-pill-amber-tx)', marginTop: 2 }}>+{money(r.overage_mrr)} overage</div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <IssueTrialDialog open={trialOpen} onClose={() => setTrialOpen(false)} onIssued={load} />
    </div>
  );
}
